import type {
  AccountTransactionChildDraft,
  AccountTransactionDraft,
  NetworkType,
  Policy,
} from '@mls-wallet/shared-types';
import { buildAccountTransactionChildDraft } from './buildAccountTransactionDraft';
import { isPolicyAvailableForTransaction } from './transaction-policy-validation';
import type {
  ContractInteractionDetails,
  TokenTransferDetails,
} from './useTransactionBuilderStore';

/** Form values belong to one child; account, network and description never do. */
export type BatchTransactionEditor =
  | {
      transactionType: 'token-transfer';
      tokenTransferDetails: TokenTransferDetails;
    }
  | {
      transactionType: 'contract-interaction';
      contractInteractionDetails: ContractInteractionDetails;
    };

export type BatchTransactionChild = {
  id: string;
  payload: AccountTransactionChildDraft;
  policy: Policy;
  /** Raw JSON children have no trusted form metadata. */
  editor?: BatchTransactionEditor;
};

export type BatchTransactionInput = Omit<BatchTransactionChild, 'id'>;

/** Encode before saving so a cart row can never represent an incomplete form. */
export function buildBatchTransactionChild(
  policy: Policy,
  editor: BatchTransactionEditor
): BatchTransactionInput {
  return {
    policy,
    editor,
    payload: buildAccountTransactionChildDraft({
      policyId: policy.id,
      ...editor,
    }),
  };
}

/** Preserve cart order and exclude display metadata and editor identities. */
export function buildBatchAccountTransactionDraft(input: {
  multichainAccountId: string;
  networkId: NetworkType;
  description: string;
  children: BatchTransactionChild[];
}): AccountTransactionDraft {
  const children = input.children.map(({ payload, policy }) => {
    // Case: reject the batch if a child's policy differs from its payload, was
    // deleted, or no longer permits the parent's network or account.
    if (
      payload.policyId !== policy.id ||
      !isPolicyAvailableForTransaction(policy, input)
    ) {
      throw new Error(
        'A transaction policy is no longer available for this account and network.'
      );
    }
    return { ...payload };
  });
  const [first, ...rest] = children;
  if (!first) throw new Error('Add at least one transaction to the batch.');
  return {
    multichainAccountId: input.multichainAccountId,
    networkId: input.networkId,
    description: input.description,
    children: [first, ...rest],
  };
}

export function getBatchTransactionTypeLabel(
  child: BatchTransactionChild
): string {
  // Case: preserve the chosen editor type even when a contract interaction has no calldata.
  if (child.editor) {
    return child.editor.transactionType === 'token-transfer'
      ? 'Token Transfer'
      : 'Contract Interaction';
  }
  return child.payload.data === '0x'
    ? 'Token Transfer'
    : 'Contract Interaction';
}
